import type { LocalizationUtilsConfig, PathnamesMapping } from "#modules/localization/pathNames/types"

import {
  applyBasePath,
  formatTemplatePathname,
  getInternalTemplate,
  getInternalTemplateWithSortedPathnames,
  getNormalizedPathname,
} from "#modules/localization/pathNames/pathNameUtils"

export type LocalizePathParams<Locale extends string> = {
  pathName: string
  locale: Locale
  sortedRouteLocalizationMapping?: string[]
  applyBaseUrl?: boolean
}
export function internalLocalizePath<Locales extends readonly string[], Locale extends Locales[number], DefaultLocale extends Locales[number]>({
  pathName,
  locale,
  defaultLocale,
  locales,
  routeLocalizationMapping,
  sortedRouteLocalizationMapping,
  keepDefaultLocale,
  baseUrl,
  applyBaseUrl,
}: LocalizationUtilsConfig<Locales, DefaultLocale> & LocalizePathParams<Locale>): string {
  //@ts-ignore error is okay
  if (!locales.includes(locale)) locale = defaultLocale

  const normalizedPathname = getNormalizedPathname(pathName, locales)
  const [, internalTemplateName] = sortedRouteLocalizationMapping
    ? getInternalTemplateWithSortedPathnames(routeLocalizationMapping, sortedRouteLocalizationMapping, pathName, locale)
    : getInternalTemplate(routeLocalizationMapping, normalizedPathname, locale)

  if (internalTemplateName) {
    let localeTemplate = getTemplate(internalTemplateName, routeLocalizationMapping, locale)

    // Check if the template has a mapping for the target locale, if not try with defaultLocale
    if (!localeTemplate) {
      localeTemplate = getTemplate(internalTemplateName, routeLocalizationMapping, defaultLocale)

      // If the template has no mapping for the defaultLocale either, fallback to the input pathname
      if (!localeTemplate) return withBaseUrl(prefixPathName({ normalizedPathname, locale, defaultLocale, keepDefaultLocale }), baseUrl, applyBaseUrl)
    }

    return withBaseUrl(
      //@ts-ignore error is okay
      formatTemplatePathname(normalizedPathname, internalTemplateName, localeTemplate, keepDefaultLocale || locale !== defaultLocale ? locale : undefined),
      baseUrl,
      applyBaseUrl,
    )
  }

  return withBaseUrl(prefixPathName({ normalizedPathname, locale, defaultLocale, keepDefaultLocale }), baseUrl, applyBaseUrl)
}

function getTemplate<Locales extends string[], Locale extends Locales[number]>(
  internalTemplateName: string,
  routeLocalizationMapping: PathnamesMapping<Locales>,
  locale: Locale,
): string | undefined {
  const pathnameConfig = routeLocalizationMapping[internalTemplateName]
  // @ts-ignore
  return typeof pathnameConfig === "string" ? pathnameConfig : pathnameConfig[locale]
}

function withBaseUrl(pathName: string, baseUrl: string, applyBaseUrl: boolean | undefined): string {
  if (!applyBaseUrl) return pathName

  return applyBasePath(pathName, baseUrl)
}

function prefixPathName({
  normalizedPathname,
  locale,
  defaultLocale,
  keepDefaultLocale,
}: {
  normalizedPathname: string
  locale: string
  defaultLocale: string
  keepDefaultLocale?: boolean
}) {
  if (keepDefaultLocale || locale !== defaultLocale) {
    if (normalizedPathname === "/") return `/${locale}`
    return `/${locale}${normalizedPathname}`
  }

  return normalizedPathname
}
